'use client';

import { useState } from 'react';

interface BlogTranslateButtonProps {
  blogPostId: string;
  translationStatus: string | null;
  translationError?: string | null;
}

export function BlogTranslateButton({ 
  blogPostId, 
  translationStatus, 
  translationError 
}: BlogTranslateButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(translationStatus);
  const [error, setError] = useState(translationError);

  const handleTranslate = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/blog/translate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ blogPostId }),
      });
      
      const result = await response.json();
      
      if (result.success) {
        setStatus('completed');
        // Reload page to show translated content
        window.location.reload();
      } else {
        setError(result.error || 'Translation failed');
        setStatus('failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setStatus('failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Don't show button if already completed
  if (status === 'completed') {
    return null;
  }

  return (
    <div className="inline-flex flex-col gap-2">
      <button
        onClick={handleTranslate}
        disabled={isLoading || status === 'processing'}
        className={`inline-flex items-center gap-2 font-medium py-2 px-4 rounded-lg transition-colors text-sm ${
          status === 'failed' 
            ? 'bg-red-600 hover:bg-red-700 text-white' 
            : status === 'processing'
            ? 'bg-yellow-500 text-white cursor-wait'
            : 'bg-gray-600 hover:bg-gray-700 text-white'
        } ${isLoading ? 'opacity-70 cursor-wait' : ''}`}
      >
        {isLoading ? (
          <>
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span>در حال ترجمه...</span>
          </>
        ) : status === 'failed' ? (
          <>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>تلاش مجدد ترجمه</span>
          </>
        ) : status === 'processing' ? (
          <>
            <svg className="animate-pulse h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>در حال پردازش</span>
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
            </svg>
            <span>ترجمه به فارسی</span>
          </>
        )}
      </button>
      
      {error && (
        <span className="text-xs text-red-600 dark:text-red-400">
          خطا: {error.substring(0, 100)}
        </span>
      )}
    </div>
  );
}